import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { map, switchMap } from 'rxjs';

import { StationService } from '@services/api/station/station.service';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { ContentFieldComponent } from '@shared/content-field/content-field.component';
import { TextButtonComponent } from '@shared/button/text-button/text-button.component';
import { AddFixersDialogComponent } from '@main/dialogs/add-fixers-dialog/add-fixers-dialog.component';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { groupAdditionalFixers } from '@helpers/shared';
import { UserInfo, UserKind } from '@models/user';
import { DiscrepancyFixers } from '@models/discrepancy';
import { AutomationRuleTypeGroup } from '@models/automation';
import { DashIfEmptyPipe } from '@pipes/dash-if-empty.pipe';
import { StationCommunicationType } from '@models/station/detail';
import DATA_QA from '@automation/data-qa.json';

/** Fixers a station offers, as loaded for the station currently selected. */
export interface StationFixerOptions {
    stationId: number;
    /** Everyone associated with the station: the additional fixer candidates. */
    all: UserInfo[];
    /** Primary fixer candidates: persons only on an in-app station. */
    primary: UserInfo[];
}

@Component({
    selector: 'app-fixer-field',
    template: `@if (readOnly) {
            <app-content-field label="FIXER(S)">
                <ng-container *ngTemplateOutlet="fixerInformationTemplate" />
            </app-content-field>
        } @else {
            <div class="pos-rel">
                <p class="label text-xxs color-gray14 mb-1">FIXER(S) *</p>
                <app-text-button
                    class="new-fixer-button"
                    [dataQa]="dataQa.addEditFixerButton"
                    [disabled]="
                        forceDisabled || primaryFixerOptions.length === 0
                    "
                    (clicked)="openNewFixerDialog()">
                    <span class="flex items-center text-sm">
                        <mat-icon
                            [svgIcon]="primaryFixer ? 'edit' : 'add'"
                            [style.width]="'18px'" />
                        {{ primaryFixer ? 'Edit' : 'New' }}
                    </span>
                </app-text-button>
                @if (loadingFixerOptions) {
                    <p class="text-s color-gray10">&nbsp;</p>
                } @else if (primaryFixerOptions.length === 0) {
                    <p class="text-s color-gray10 text-wrap">
                        No fixer found in this station.
                    </p>
                } @else {
                    <p class="fixer text-s">
                        @if (primaryFixer) {
                            <ng-container
                                *ngTemplateOutlet="fixerInformationTemplate" />
                        } @else {
                            <span class="text-s color-gray10">
                                No fixer added yet.
                            </span>
                        }
                    </p>
                }
            </div>
        }
        <ng-template #fixerInformationTemplate>
            @if (primaryFixer) {
                <span class="text-s text-semi">Primary: </span>
                {{ primaryFixer.fullName | dashIfEmpty }}
                ({{ primaryFixer.email }})
                @if (secondaryFixers.length > 0) {
                    <div class="flex items-center color-gray13">
                        Additional: {{ additionalInfo }}
                        <app-tooltip>
                            <mat-icon trigger svgIcon="info" />
                            <div content>
                                @for (
                                    fixers of groupedAdditionalFixers;
                                    track $index
                                ) {
                                    @if (fixers.items.length > 0) {
                                        <p class="color-gray12 text-semi mb-1">
                                            {{ fixers.group }} ({{
                                                fixers.items.length
                                            }})
                                        </p>
                                        <ul>
                                            @for (
                                                item of fixers.items;
                                                track $index
                                            ) {
                                                <li>{{ item }}</li>
                                            }
                                        </ul>
                                    }
                                }
                            </div>
                        </app-tooltip>
                    </div>
                }
            } @else {
                -
            }
        </ng-template>`,
    styles: `
        .new-fixer-button {
            right: 0;
            position: absolute;
            top: -5px;
        }

        .fixer {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }
    `,
    imports: [
        NgTemplateOutlet,
        DashIfEmptyPipe,
        ContentFieldComponent,
        MatIconModule,
        TextButtonComponent,
        TooltipComponent,
    ],
})
export class FixerFieldComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) id!: number;
    @Input({ required: true }) value = DiscrepancyFixers.initial();
    @Input({ required: true }) stationId: number | undefined;
    @Input({ required: true }) readOnly: boolean = true;
    @Input() forceDisabled: boolean = false;
    /**
     * Externally suggested fixer IDs (e.g. coming from automation rules).
     * When this Input changes, the component looks up matching UserInfo from
     * its loaded fixer options and applies them as the active fixers. If the
     * options have not loaded yet, the request is queued and applied once they
     * arrive.
     */
    @Input() externalFixerIds: {
        primary: number | null;
        secondary: number[];
    } | null = null;
    @Input() automationRuleGroups: AutomationRuleTypeGroup[] = [];
    @Output() fixerUpdated = new EventEmitter<DiscrepancyFixers>();
    /**
     * The station's fixers, published whenever they are loaded. Automation
     * rules are configured over every station, so the parent needs them to
     * tell an applicable fixer output from one this station cannot hold.
     */
    @Output() fixerOptionsLoaded = new EventEmitter<StationFixerOptions>();
    fixerOptions: UserInfo[] = [];
    primaryFixerOptions: UserInfo[] = [];
    loadingFixerOptions: boolean = false;
    additionalInfo: string = '';
    groupedAdditionalFixers: { group: string; items: string[] }[] = [];
    private pendingExternalFixerIds: {
        primary: number | null;
        secondary: number[];
    } | null = null;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    readonly dataQa = DATA_QA;

    private readonly dialog = inject(MatDialog);
    private readonly stationService = inject(StationService);

    get primaryFixer(): UserInfo | null {
        return this.value.primaryFixer;
    }

    get secondaryFixers(): UserInfo[] {
        return this.value.secondaryFixers;
    }

    ngOnInit(): void {
        this.prepareFixerData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const stationIdChange = changes['stationId'];

        if (stationIdChange) {
            this.getFixerOptions();

            if (!stationIdChange.firstChange) {
                this.value = DiscrepancyFixers.initial();
            }
        }

        const externalFixerIdsChange = changes['externalFixerIds'];
        if (externalFixerIdsChange && this.externalFixerIds) {
            this.pendingExternalFixerIds = this.externalFixerIds;
            if (this.fixerOptions.length > 0) {
                this.applyPendingExternalFixerIds();
            }
        }
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribeAllSafe();
    }

    protected openNewFixerDialog() {
        this.dialog
            .open(AddFixersDialogComponent, {
                data: {
                    fixerOptions: this.fixerOptions,
                    primaryFixerOptions: this.primaryFixerOptions,
                    currentFixers: this.value,
                    automationRuleGroups: this.automationRuleGroups,
                    stationId: this.stationId,
                },
                maxWidth: '700px',
                minWidth: '200px',
                width: '700px',
                maxHeight: '90vh',
                disableClose: true,
            })
            .afterClosed()
            .subscribe({
                next: (response: { discrepancyFixers: DiscrepancyFixers }) => {
                    if (response) {
                        this.value = response.discrepancyFixers;
                        this.prepareFixerData();
                        this.fixerUpdated.emit(response.discrepancyFixers);
                    }
                },
            });
    }

    private prepareFixerData() {
        this.setAdditionalFixerInfo();
        this.setGroupedAdditionalFixers();
    }

    private setAdditionalFixerInfo() {
        const fixers = [];
        const { secondaryFixers } = this.value;
        const { DistributionList, Person } = UserKind;
        const { dlCount, peopleCount } = secondaryFixers.reduce(
            (acc, item) => {
                if (item.kind === DistributionList) acc.dlCount++;
                if (item.kind === Person) acc.peopleCount++;
                return acc;
            },
            { dlCount: 0, peopleCount: 0 },
        );

        if (dlCount > 0) fixers.push(`${dlCount} Distribution List(s)`);
        if (peopleCount > 0) fixers.push(`${peopleCount} People`);

        this.additionalInfo = fixers.join(', ');
    }

    private getFixerOptions() {
        const stationId = this.stationId;
        if (stationId) {
            this.fixerOptions = [];
            this.primaryFixerOptions = [];
            this.loadingFixerOptions = true;

            this._subscriptions['get-fixer-options'] = this.stationService
                .getStationFixers(stationId)
                .pipe(
                    switchMap((fixers) =>
                        this.stationService
                            .getStationLookup(stationId)
                            .pipe(map((lookup) => ({ fixers, lookup }))),
                    ),
                )
                .subscribe({
                    next: ({ fixers, lookup }) => {
                        this.fixerOptions = fixers;
                        this.primaryFixerOptions =
                            lookup?.communicationType ===
                            StationCommunicationType.IN_APP
                                ? fixers.filter(
                                      (i) => i.kind === UserKind.Person,
                                  )
                                : fixers;
                        this.loadingFixerOptions = false;
                        this.fixerOptionsLoaded.emit({
                            stationId,
                            all: this.fixerOptions,
                            primary: this.primaryFixerOptions,
                        });
                        this.applyPendingExternalFixerIds();
                    },
                    error: () => {
                        this.loadingFixerOptions = false;
                        // Published so a caller waiting for this station's
                        // fixers is not left waiting for good.
                        this.fixerOptionsLoaded.emit({
                            stationId,
                            all: [],
                            primary: [],
                        });
                    },
                });
        }
    }

    private applyPendingExternalFixerIds(): void {
        if (!this.pendingExternalFixerIds) return;

        const { primary, secondary } = this.pendingExternalFixerIds;
        const primaryFixer =
            primary != null
                ? (this.fixerOptions.find((f) => f.id === primary) ?? null)
                : null;
        const secondaryFixers = secondary
            .map((id) => this.fixerOptions.find((f) => f.id === id))
            .filter((f): f is UserInfo => !!f);

        const next: DiscrepancyFixers = { primaryFixer, secondaryFixers };
        this.value = next;
        this.prepareFixerData();
        // Do NOT emit fixerUpdated here. The parent form already contains the
        // correct IDs (set by automation). Emitting would cause a form patch
        // WITHOUT emitEvent:false, marking the form as dirty (Bug 121389).
        this.pendingExternalFixerIds = null;
    }

    private setGroupedAdditionalFixers() {
        this.groupedAdditionalFixers = groupAdditionalFixers(
            this.secondaryFixers,
        );
    }
}
